import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";
import CustomerPortalLogin from "@/components/public/CustomerPortalLogin";
import CustomerPortalDashboard from "@/components/public/CustomerPortalDashboard";
import { prepareCustomerAvatar, customerAvatarKey, hydrateCustomerAvatar } from "@/utils/customerAvatar";
import { usePortalPrime } from "@/hooks/usePortalPrime";
const API = API_URL;
export default function CustomerPortalPage() {
  const { restaurantId } = useParams();
  const [step, setStep] = useState("phone"); // phone | code | logged
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [restaurantPhone, setRestaurantPhone] = useState("");
  const [waAutoSent, setWaAutoSent] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [referralData, setReferralData] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const { primeStatus, primeOffers, refreshPrime, settlePendingPrimePayment, cancelPrime } = usePortalPrime(restaurantId);
  useEffect(() => {
    axios.get(`${API}/api/public/menu/${restaurantId}`).then(res => setRestaurant({ ...(res.data.restaurant || {}), logo_url: res.data.restaurant?.logo_url || res.data.theme?.logo_url || "" })).catch(() => {});
    const savedPhone = localStorage.getItem(`customer_phone_${restaurantId}`);
    const savedToken = localStorage.getItem(`customer_token_${restaurantId}`);
    if (savedPhone) checkSession(savedPhone, savedToken);
  }, [restaurantId]);
  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);
  const checkSession = async (savedPhone, savedToken) => {
    if (!savedToken) {
      localStorage.removeItem(`customer_phone_${restaurantId}`);
      return;
    }
    try {
      const res = await axios.get(
        `${API}/api/public/customer-session/${restaurantId}?phone=${savedPhone}`,
        { headers: { "X-Session-Token": savedToken } }
      );
      if (res.data.active) {
        setPhone(savedPhone);
        setCustomer(hydrateCustomerAvatar(res.data.customer, restaurantId, savedPhone));
        setStep("logged");
        fetchProfile(savedToken, savedPhone);
        await settlePendingPrimePayment();
        await refreshPrime(savedPhone);
      }
    } catch {
      localStorage.removeItem(`customer_phone_${restaurantId}`);
      localStorage.removeItem(`customer_token_${restaurantId}`);
    }
  };
  const fetchProfile = async (sessionToken, phoneValue = phone || localStorage.getItem(`customer_phone_${restaurantId}`)) => {
    try {
      const res = await axios.get(
        `${API}/api/public/customer/profile/${restaurantId}`,
        { headers: { "X-Session-Token": sessionToken } }
      );
      setCustomer(hydrateCustomerAvatar(res.data.customer, restaurantId, phoneValue));
      setOrders(res.data.orders || []);
    } catch (e) {
      console.error("Error fetching profile:", e);
      if (e.response?.status === 401) {
        logout();
      }
    }
  };
  const updateAvatar = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Elige una imagen válida"); return; }
    const dataUrl = await prepareCustomerAvatar(file).catch(() => null);
    if (!dataUrl) { toast.error("No se pudo preparar la imagen"); return; }
    const sessionToken = localStorage.getItem(`customer_token_${restaurantId}`);
    try {
      await axios.put(`${API}/api/public/customer/profile/avatar/${restaurantId}`, { avatar_url: dataUrl }, { headers: { "X-Session-Token": sessionToken } });
      setCustomer(prev => ({ ...prev, avatar_url: dataUrl }));
      localStorage.setItem(customerAvatarKey(restaurantId, phone), dataUrl);
      localStorage.setItem(`customer_avatar_${restaurantId}`, dataUrl);
      toast.success("Foto de perfil actualizada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar la foto");
    }
  };
  const saveAddress = async (address) => {
    const token = localStorage.getItem(`customer_token_${restaurantId}`);
    try {
      const res = await axios.put(`${API}/api/public/customer/profile/address/${restaurantId}`, { address }, { headers: { "X-Session-Token": token } });
      setCustomer(prev => ({ ...prev, addresses: res.data.addresses || [] }));
      toast.success("Dirección guardada");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar la dirección"); }
  };
  const deleteAddress = async (addressId) => {
    const token = localStorage.getItem(`customer_token_${restaurantId}`);
    try {
      const res = await axios.delete(`${API}/api/public/customer/profile/address/${restaurantId}/${addressId}`, { headers: { "X-Session-Token": token } });
      setCustomer(prev => ({ ...prev, addresses: res.data.addresses || [] }));
      toast.success("Dirección eliminada");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar la dirección"); }
  };
  const requestCode = async () => {
    if (!phone || phone.length < 9) {
      toast.error("Introduce un número de teléfono válido");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/public/customer/request-code/${restaurantId}`, { phone });
      setStep("code");
      setWaAutoSent(true);
      if (res.data.restaurant_phone) setRestaurantPhone(res.data.restaurant_phone);
      if (res.data.whatsapp_sent) {
        setCountdown(60);
        toast.success("¡Código enviado por WhatsApp!");
      } else {
        toast.info("WhatsApp está listo para enviar el mensaje. Confirma el envío y escribe aquí el código que recibirás.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar el código");
    }
    setLoading(false);
  };
  const verifyCode = async () => {
    if (!code || code.length !== 6) {
      toast.error("Introduce el código de 6 dígitos");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/public/customer/verify-code/${restaurantId}`, { phone, code, name });
      const sessionToken = res.data.session_token;
      localStorage.setItem(`customer_phone_${restaurantId}`, phone);
      localStorage.setItem(`customer_token_${restaurantId}`, sessionToken);
      setCustomer(hydrateCustomerAvatar(res.data.customer, restaurantId, phone));
      setOrders(res.data.orders || []);
      setStep("logged");
      await settlePendingPrimePayment();
      await refreshPrime(phone);
      toast.success("¡Has iniciado sesión correctamente!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Código no válido");
    }
    setLoading(false);
  };
  const logout = () => {
    localStorage.removeItem(`customer_phone_${restaurantId}`);
    localStorage.removeItem(`customer_token_${restaurantId}`);
    setCustomer(null);
    setOrders([]);
    setStep("phone");
    setPhone("");
    setCode("");
    toast.success("Sesión cerrada");
  };
  if (step !== "logged") {
    return (
      <CustomerPortalLogin
        restaurantId={restaurantId}
        restaurantLogo={restaurant?.logo_url}
        restaurantName={restaurant?.name}
        step={step}
        phone={phone} setPhone={setPhone}
        code={code} setCode={setCode}
        name={name} setName={setName}
        loading={loading}
        countdown={countdown}
        waAutoSent={waAutoSent}
        restaurantPhone={restaurantPhone}
        onRequestCode={requestCode}
        onVerifyCode={verifyCode}
        onBack={() => setStep("phone")}
      />
    );
  }
  return (
    <CustomerPortalDashboard
      restaurantId={restaurantId}
      customer={customer}
      orders={orders}
      referralData={referralData}
      primeStatus={primeStatus}
      primeOffers={primeOffers}
      onAvatarChange={updateAvatar}
      restaurant={restaurant}
      onSaveAddress={saveAddress}
      onDeleteAddress={deleteAddress}
      onLogout={logout}
      onPrimeCancel={cancelPrime}
    />
  );
}
